import React, { useEffect, useState } from "react";
import { getRequest } from "../../service";
import { getEmployeeId } from "../../utils/localStorage";
import { formatDate } from "../../utils/dateTime";
import { Spin, Pagination, Tag } from "antd";
import { Edit } from "lucide-react";
import LeaveRequest from "../../modal/admin/leaveRequestModal";
import AttendanceLogRequest from "../../modals/attendanceLogReq";
import ApprovedOverTime from '../../modals/approvedOverTime';
import ModalWrapper from "../../Modal";

const RequestTable = () => {
  const heading = ["Req Type", "Requested By", "Date", "Status", "Action"];
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(1);
  const pageSize = 5;

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editShiftModalOpen, setEditShiftModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editData, setEditData] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Data fetching function
  const fetchData = async () => {
    setLoading(true);
    try {
      const employeeId = getEmployeeId();
      const [shiftRes, leaveRes, overtimeRes] = await Promise.all([
        getRequest(`/api/requestshift`),
        employeeId ? getRequest(`/api/leaveRequest`) : Promise.resolve({ leaveRequests: [] }),
        getRequest(`/api/overtimerequest`)
      ]);
      const shiftRequests = (shiftRes?.requestShifts || []).map(item => ({
        reqType: "Missing Attendance",
        requestedBy: item.employee?.firstName && item.employee?.lastName
          ? `${item.employee.firstName} ${item.employee.lastName}`
          : "N/A",
        date: item.createdAt,
        status: item.status,
        _id: item._id || item.createdAt, // fallback for key
        originalData: item, // Store original data for modal
        type: "attendance"
      }));
      const leaveRequests = (leaveRes?.leaveRequests || []).map(item => ({
        reqType: "Leave",
        requestedBy: item.employeeInfo?.name || "N/A",
        date: item.createdAt,
        status: item.status,
        _id: item._id || item.createdAt, // fallback for key
        originalData: item, // Store original data for modal
        type: "leave"
      }));
      const overtimeRequests = (overtimeRes?.overtimeRequests || []).map(item => ({
        reqType: "Overtime",
        requestedBy: item.employee?.firstName && item.employee?.lastName
          ? `${item.employee.firstName} ${item.employee.lastName}`
          : "N/A",
        date: item.createdAt,
        status: item.status,
        _id: item._id || item.createdAt, // fallback for key
        originalData: item, // Store original data for modal
        type: "overtime",
        // Add the transformed data structure that ApprovedOverTime modal expects
        employeeName: `${item.employee?.firstName || ''} ${item.employee?.lastName || ''}`.trim(),
        checkIn: item.attendanceLog?.checkIn || 'N/A',
        checkOut: item.attendanceLog?.checkOut || 'N/A',
        workingHour: item.attendanceLog?.workingHour || 'N/A',
        totalOverTime: item.totalOverTime || 'N/A',
        paidOverTime: item.paidOverTime || 'N/A',
        approvedBy: item.approvedBy ? `${item.approvedBy.firstName || ''} ${item.approvedBy.lastName || ''}`.trim() : null,
        employeeId: item.employeeId || '',
        id: item._id || ''
      }));

      // Filter to show only pending requests
      const allRequests = [...shiftRequests, ...leaveRequests, ...overtimeRequests];
      const pendingRequests = allRequests.filter(item =>
        (item.status || "").toLowerCase() === "pending"
      );

      setData(pendingRequests);
    } catch (err) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Helper to get Tag color and style based on status
  const getStatusTag = (status) => {
    const normalized = (status || "").toLowerCase();
    if (normalized === "pending") {
      return (
        <Tag
          color="#fff7e6"
          style={{
            color: "#faad14",
            borderColor: "#ffe58f",
            background: "#fff7e6",
            fontWeight: 500,
            fontSize: 12,
            padding: "2px 10px",
            borderRadius: 6,
            borderWidth: 1,
            borderStyle: "solid",
            display: "inline-block",
            minWidth: 60,
            textAlign: "center",
          }}
        >
          Pending
        </Tag>
      );
    }
    if (normalized === "approved") {
      return (
        <Tag
          color="#f6ffed"
          style={{
            color: "#52c41a",
            borderColor: "#b7eb8f",
            background: "#f6ffed",
            fontWeight: 500,
            fontSize: 14,
            padding: "2px 12px",
            borderRadius: 6,
            borderWidth: 1,
            borderStyle: "solid",
            display: "inline-block",
            minWidth: 60,
            textAlign: "center",
          }}
        >
          Approved
        </Tag>
      );
    }
    if (normalized === "rejected") {
      return (
        <Tag
          color="#fff1f0"
          style={{
            color: "#ff4d4f",
            borderColor: "#ffa39e",
            background: "#fff1f0",
            fontWeight: 500,
            fontSize: 10,
            padding: "1px 12px",
            borderRadius: 6,
            borderWidth: 1,
            borderStyle: "solid",
            display: "inline-block",
            minWidth: 60,
            textAlign: "center",
          }}
        >
          Rejected
        </Tag>
      );
    }
    // Default
    return (
      <Tag
        style={{
          color: "#595959",
          borderColor: "#d9d9d9",
          background: "#fafafa",
          fontWeight: 500,
          fontSize: 14,
          padding: "2px 12px",
          borderRadius: 6,
          borderWidth: 1,
          borderStyle: "solid",
          display: "inline-block",
          minWidth: 60,
          textAlign: "center",
        }}
      >
        {status}
      </Tag>
    );
  };

  // Pagination logic
  const startIdx = (current - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const pageData = data.slice(startIdx, endIdx);

    // Handler for edit action
  const handleEdit = (item) => {
    console.log("Edit clicked for:", item);
    
    // Set the appropriate modal and data based on request type
    if (item.type === "leave") {
      setEditData(item.originalData);
      setEditModalOpen(true);
    } else if (item.type === "attendance") {
      setSelectedRequest(item.originalData);
      setIsModalOpen(true);
    } else if (item.type === "overtime") {
      // For overtime, pass the transformed data structure that matches what ApprovedOverTime expects
      setSelectedEntry({
        employeeName: item.employeeName,
        date: item.date,
        checkIn: item.checkIn,
        checkOut: item.checkOut,
        workingHour: item.workingHour,
        totalOverTime: item.totalOverTime,
        paidOverTime: item.paidOverTime,
        status: item.status,
        approvedBy: item.approvedBy,
        employeeId: item.employeeId,
        id: item.id,
        originalData: item.originalData // Keep original data for details field
      });
      setEditShiftModalOpen(true);
    }
  };

  // Modal close handlers
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditData(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRequest(null);
  };

  const handleCloseEditShiftModal = () => {
    setEditShiftModalOpen(false);
    setSelectedEntry(null);
  };

  // Success handlers that refresh data instead of reloading page
  const handleLeaveSuccess = () => {
    handleCloseEditModal();
    fetchData(); // Refresh data instead of reloading page
  };

  const handleAttendanceSuccess = () => {
    handleCloseModal();
    fetchData(); // Refresh data instead of reloading page
  };

  const handleOvertimeSuccess = () => {
    handleCloseEditShiftModal();
    fetchData(); // Refresh data instead of reloading page
  };

  return (
    <>
      <Spin spinning={loading} size="large">
        <div className="w-full overflow-x-auto">
          <table className="min-w-[600px] w-full bg-white ">
            <thead>
              <tr className="text-left border-b border-gray-200">
                {heading.map((item) => (
                  <th
                    className="px-2 py-2 md:px-4 md:py-2 text-[13px] md:text-[14px] font-semibold text-primary whitespace-nowrap"
                    key={item}
                  >
                    {item}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.length === 0 ? (
                <tr>
                  <td colSpan={heading.length} className="px-2 py-4 md:px-4 text-center text-gray-500">
                    No requests found.
                  </td>
                </tr>
              ) : (
                pageData.map((item) => (
                  <tr className="border-b border-gray-200" key={item._id}>
                    <td className="px-2 py-2 md:px-4 md:py-1 text-[13px] md:text-[14px] text-gray-700 whitespace-nowrap">{item.reqType}</td>
                    <td className="px-2 py-2 md:px-4 md:py-1 text-[13px] md:text-[14px] text-gray-700 whitespace-nowrap">{item.requestedBy}</td>
                    <td className="px-2 py-2 md:px-4 md:py-1 text-[13px] md:text-[14px] text-gray-700 whitespace-nowrap">{formatDate(item.date)}</td>
                    <td className="px-2 py-2 md:px-4 md:py-1 text-[13px] md:text-[14px] text-gray-700 whitespace-nowrap">
                      {getStatusTag(item.status)}
                    </td>
                    <td className="px-2 py-2 md:px-4 md:py-1 text-center">
                      <button
                        onClick={() => handleEdit(item)}
                        className="hover:bg-gray-100 rounded p-1 transition"
                        title="Edit"
                        type="button"
                      >
                        <Edit size={18} className="text-primary" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end w-full mt-2">
          <Pagination
            current={current}
            pageSize={pageSize}
            total={data.length}
            onChange={setCurrent}
            showSizeChanger={false}
            className="!w-auto"
          />
        </div>
      </Spin>
      
      {/* Leave Request Modal */}
      <ModalWrapper
        open={editModalOpen}
        onRequestClose={handleCloseEditModal}
        title="Edit Leave Request"
      >
        <LeaveRequest
          initialData={editData}
          onClose={handleCloseEditModal}
          onSuccess={handleLeaveSuccess}
        />
      </ModalWrapper>
      
      {/* Attendance Log Request Modal */}
      <ModalWrapper
        open={isModalOpen}
        onRequestClose={handleCloseModal}
        title="Attendance Request Details"
      >
        <AttendanceLogRequest
          onClose={handleCloseModal}
          requestData={selectedRequest}
          onSuccess={handleAttendanceSuccess}
        />
      </ModalWrapper>
      
      {/* Overtime Request Modal */}
      <ModalWrapper
        open={editShiftModalOpen}
        onRequestClose={handleCloseEditShiftModal}
        title="Overtime Request Details"
      >
        <ApprovedOverTime
          onClose={handleCloseEditShiftModal}
          onSuccess={handleOvertimeSuccess}
          initialData={selectedEntry || {}}
        />
      </ModalWrapper>
    </>
  );
};

export default RequestTable;
